import sanitizeHtml from 'sanitize-html';

/**
 * Balises et attributs autorisés pour le formatage riche des règles de ligue.
 * Volontairement restrictive : uniquement du texte structuré, pas d'images,
 * pas de styles inline, pas de scripts.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'a',
  'h2',
  'h3',
  'h4',
];

const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: ALLOWED_TAGS,
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  // Force ouverture en nouvel onglet + sécurité rel
  transformTags: {
    a: (tagName, attribs) => ({
      tagName: 'a',
      attribs: {
        href: attribs.href || '#',
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
      },
    }),
  },
  // Interdit tout ce qui ne matche pas allowedSchemes
  allowedSchemesByTag: {
    a: ['http', 'https', 'mailto'],
  },
  disallowedTagsMode: 'discard',
};

const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*?>/i;

/**
 * Convertit du texte brut existant (historique) en HTML minimal :
 * - `\n\n` → séparateur de paragraphes
 * - `\n` seul → `<br>`
 * - Échappe les caractères HTML dangereux avant l'insertion.
 */
function plainTextToHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const paragraphs = escaped
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)
    .map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('');

  return paragraphs;
}

/**
 * Normalise puis sanitise du contenu de règles de ligue.
 * Accepte à la fois de l'HTML (nouveau format) et du texte brut (historique).
 */
export function sanitizeRichText(input: string | null | undefined): string {
  if (!input) return '';
  const trimmed = input.trim();
  if (!trimmed) return '';

  const html = HTML_TAG_REGEX.test(trimmed) ? trimmed : plainTextToHtml(trimmed);
  return sanitizeHtml(html, SANITIZE_OPTIONS);
}

/**
 * Retourne true si le contenu ne comporte pas de texte visible après nettoyage.
 * Utile pour distinguer un `<p></p>` vide (état par défaut de l'éditeur) d'un
 * contenu réel à sauvegarder.
 */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  // Utilise le parser HTML de sanitize-html (allow-list vide) pour extraire
  // uniquement le texte visible, sans dépendre d'une regex sur du HTML.
  const textOnly = sanitizeHtml(html, {
    allowedTags: [],
    allowedAttributes: {},
  });
  const stripped = textOnly.replace(/&nbsp;/gi, '').replace(/\s+/g, '').trim();
  return stripped.length === 0;
}
